import { useState, useEffect, useRef } from 'react';

type Message = {
  id: string;
  role: 'user' | 'bot';
  text: string;
};

const SUGGESTIONS = [
  "I'd like to make a reservation",
  "What time do you open?",
  "Where are you located?",
];

function generateSessionId(): string {
  return crypto.randomUUID();
}

export const LineBotChatPage = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [statusText, setStatusText] = useState<string | null>(null);
  // Fresh session every page load — intentionally not persisted
  const [sessionId] = useState<string>(() => generateSessionId());
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-focus input on page load
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  const sendMessage = async (overrideText?: string) => {
    const text = (overrideText ?? input).trim();
    if (!text || isLoading) return;

    const userMessage: Message = { id: crypto.randomUUID(), role: 'user', text };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const res = await fetch('/api/line-bot-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, sessionId }),
      });

      if (!res.body) throw new Error('No response body');

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let reply = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // Parse SSE events from buffer
        const events = buffer.split('\n\n');
        buffer = events.pop() ?? '';

        for (const block of events) {
          const eventMatch = block.match(/^event: (\w+)/);
          const dataMatch  = block.match(/^data: (.+)$/m);
          if (!eventMatch || !dataMatch) continue;

          const eventType = eventMatch[1];
          let payload: Record<string, string>;
          try { payload = JSON.parse(dataMatch[1]); } catch { continue; }

          if (eventType === 'status') {
            setStatusText(payload.status ?? null);
          } else if (eventType === 'reply') {
            reply = payload.reply ?? '';
          } else if (eventType === 'error') {
            reply = payload.error ?? 'Something went wrong.';
          }
        }
      }

      setMessages((prev) => [
        ...prev,
        { id: crypto.randomUUID(), role: 'bot', text: reply || 'Something went wrong.' },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        { id: crypto.randomUUID(), role: 'bot', text: 'Connection error. Please try again.' },
      ]);
    } finally {
      setIsLoading(false);
      setStatusText(null);
      // Small delay so the DOM settles before focusing
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleSuggestion = (text: string) => {
    sendMessage(text);
  };

  const resetSession = () => {
    window.location.reload();
  };

  const showSuggestions = messages.length === 0 && !isLoading;

  return (
    <div
      className="h-screen flex flex-col"
      style={{ background: '#0d0d0d', color: '#e8e0d4', fontFamily: "'Inter', 'Helvetica Neue', sans-serif" }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-5 py-4 shrink-0"
        style={{ borderBottom: '1px solid #2a2a2a' }}
      >
        <div>
          <div className="flex items-center gap-2">
            <span
              className="text-xs tracking-[0.2em] uppercase font-semibold"
              style={{ color: '#c8993e' }}
            >
              Homebase
            </span>
          </div>
          <p
            className="text-[10px] tracking-[0.15em] uppercase mt-0.5"
            style={{ color: '#555' }}
          >
            Clubhouse &amp; Bar — Reservations
          </p>
        </div>
        <button
          onClick={resetSession}
          title="New conversation"
          className="transition-colors p-1.5 rounded"
          style={{ color: '#444' }}
          onMouseEnter={(e) => (e.currentTarget.style.color = '#c8993e')}
          onMouseLeave={(e) => (e.currentTarget.style.color = '#444')}
        >
          {/* Refresh icon */}
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
            <path d="M3 3v5h5" />
          </svg>
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-5 space-y-3">
        {showSuggestions && (
          <div className="flex flex-col items-center gap-6 mt-12 px-2">
            <p className="text-xs tracking-[0.2em] uppercase" style={{ color: '#444' }}>
              How can we help?
            </p>
            <div className="flex flex-col gap-2 w-full max-w-xs">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => handleSuggestion(s)}
                  className="text-sm py-2.5 px-4 rounded-full text-left transition-all"
                  style={{
                    border: '1px solid #2e2a24',
                    color: '#a89070',
                    background: 'transparent',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#c8993e';
                    e.currentTarget.style.color = '#c8993e';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#2e2a24';
                    e.currentTarget.style.color = '#a89070';
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            style={{ animation: 'msgIn 0.18s ease-out both' }}
          >
            <div
              className="max-w-[78%] px-4 py-2.5 rounded-2xl text-sm whitespace-pre-wrap break-words leading-relaxed"
              style={
                msg.role === 'user'
                  ? {
                      background: '#c8993e',
                      color: '#0d0d0d',
                      borderBottomRightRadius: '4px',
                      fontWeight: 500,
                    }
                  : {
                      background: '#1a1a1a',
                      color: '#d8cfc4',
                      border: '1px solid #2a2520',
                      borderBottomLeftRadius: '4px',
                    }
              }
            >
              {msg.text}
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex justify-start" style={{ animation: 'msgIn 0.18s ease-out both' }}>
            <div
              className="px-4 py-3 rounded-2xl flex items-center gap-2.5"
              style={{
                background: '#1a1a1a',
                border: '1px solid #2a2520',
                borderBottomLeftRadius: '4px',
              }}
            >
              <div className="flex gap-1.5 items-center shrink-0">
                <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: '#c8993e', animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: '#c8993e', animationDelay: '150ms' }} />
                <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: '#c8993e', animationDelay: '300ms' }} />
              </div>
              {statusText && (
                <span className="text-xs" style={{ color: '#7a6a58' }}>{statusText}</span>
              )}
            </div>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div
        className="px-4 py-3 flex gap-2 items-center shrink-0"
        style={{ borderTop: '1px solid #1e1e1e', background: '#0d0d0d' }}
      >
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask anything…"
          disabled={isLoading}
          className="flex-1 text-sm px-4 py-2.5 rounded-full outline-none disabled:opacity-40 transition-all"
          style={{
            background: '#1a1a1a',
            border: '1px solid #2a2520',
            color: '#e8e0d4',
          }}
          onFocus={(e) => (e.currentTarget.style.borderColor = '#c8993e')}
          onBlur={(e) => (e.currentTarget.style.borderColor = '#2a2520')}
        />
        <button
          onClick={() => sendMessage()}
          disabled={isLoading || !input.trim()}
          title="Send"
          className="w-10 h-10 flex items-center justify-center rounded-full shrink-0 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
          style={{ background: '#c8993e', color: '#0d0d0d' }}
          onMouseEnter={(e) => { if (!e.currentTarget.disabled) e.currentTarget.style.background = '#d4a84e'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = '#c8993e'; }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="22" y1="2" x2="11" y2="13" />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
        </button>
      </div>

      {/* Keyframe animation injected once */}
      <style>{`
        @keyframes msgIn {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        input::placeholder { color: #3a3530; }
      `}</style>
    </div>
  );
};
